import { Radio, RadioGroup } from "@blueprintjs/core";
import {
  ButtonsBloc,
  FGCustomInput,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import {
  EMembershipRequestDecision,
  EMembershipRequestType,
  ERequestState,
  MembershipRequestApi,
  MembershipRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
} from "../../../../api";
import {
  CancelButton,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IMembershipRequestDetailPageProps {
  MembershipRequestId: number;
  editMode: boolean;
}
const Container = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

export const MembershipRequestDetailPage: React.FunctionComponent<IMembershipRequestDetailPageProps> = ({
  MembershipRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(MembershipRequestApi);
  const requestApi = useApiService(RequestApi);
  const personApi = useApiService(PersonApi);
  const { showDialogPromise } = useDialog();
  const { openNewTab } = useWindow();
  const globalData = useGlobalData();
  const [MembershipRequestType, setMembershipRequestType] = React.useState<EMembershipRequestType>();
  const [PersonPreviousMembershipTypeId, setPersonPreviousMembershipTypeId] = React.useState<number>();
  const [DecisionCode, setDecisionCode] = React.useState<EMembershipRequestDecision>();
  const [Membershiptype, mtloading] = useReferential(
    (a) => a.getMembershipTypes(globalData.currentLanguage.languageId),
    true,
    []
  );
  const [membershipRequestTypes] = useReferential(
    (a) => a.getMembershipRequestType(globalData.currentLanguage.languageId),
    true,
    []
  );
  const [Decisions, dloading] = useReferential(
    (a) => a.getMembershipDecisions(globalData.currentLanguage.languageId),
    true,
    []
  );

  const { data, loading, saving, saveItem, refresh } = useCrudApi<MembershipRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = MembershipRequestId) =>
          id > 0
            ? api.get(id)
            : new MembershipRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: (d: MembershipRequestDto) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.membershipRequest}/${d.membershipRequestId}/detail/edit`,
      }),
      [MembershipRequestId, api]
    ),
    [MembershipRequestId]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (MembershipRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [MembershipRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      membershipRequestTypeId: Yup.number().nullable().required(t(ETLCodes.Required)),
      membershipInitialTypeId: Yup.number().nullable().required(t(ETLCodes.Required)),
      membershipRequestDecisionId: Yup.number()
        .nullable()
        .test("isDecisionPossible", t(ETLCodes.DecisionNotPossibleForMembershipRequestType), function (value) {
          if (MembershipRequestType === EMembershipRequestType.MembershipTypeChange) {
            return DecisionCode !== EMembershipRequestDecision.OtherType;
          }
          return true;
        }),
      membershipFinalTypeId: Yup.number()
        .nullable()
        .test("isDifferentFromInitial", t(ETLCodes.FinalTypeMustBeDifferentFromInitialtype), function (value) {
          if (MembershipRequestType === EMembershipRequestType.MembershipTypeChange) {
            return value !== this.parent.membershipInitialTypeId;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (MembershipRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (MembershipRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("is-available", t(ETLCodes.PersonAllreadyHasMembershipRequest), async function (value) {
            const result = await api.isUserAvailable(value, MembershipRequestId);
            console.log("value", result.value);
            return result.value;
          })
          .test("has-no-membership", t(ETLCodes.ContactIsAlreadyAMember), async function (value) {
            if (MembershipRequestType === EMembershipRequestType.NewMembership)
              return PersonPreviousMembershipTypeId === 0;
            return true;
          })
          .test("is-already-renewed", t(ETLCodes.ContactIsAlreadyAMember), async function (value) {
            if (MembershipRequestType === EMembershipRequestType.MembershipTypeChange) return true;
            return !(await api.isUserRenewed(value)).value;
          })
          .test("has-membership", t(ETLCodes.ContactWasNotAMember), async function (value) {
            if (
              MembershipRequestType !== EMembershipRequestType.NewMembership &&
              MembershipRequestType !== EMembershipRequestType.MembershipTypeChange
            )
              return PersonPreviousMembershipTypeId > 0;
            return true;
          })
          .test("is-currently-member", t(ETLCodes.IsNotCurrentlyAMember), async function (value) {
            if (MembershipRequestType === EMembershipRequestType.MembershipTypeChange) {
              return (await api.isUserRenewed(value)).value;
            }

            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [
    DecisionCode,
    MembershipRequestId,
    MembershipRequestType,
    PersonPreviousMembershipTypeId,
    api,
    data,
    personApi,
    t,
  ]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, refresh]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED || ERequestState.AC_DECISION;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const getMembershipRequestTypeCode = React.useCallback(
    async (typeId: number) => {
      return await api.getMembershipRequestTypeCode(typeId);
    },
    [api]
  );
  const getMembershipRequestDecisionCode = React.useCallback(
    async (decisionId: number) => {
      return await api.getMembershipDecisionCode(decisionId);
    },
    [api]
  );
  const getMemberMembershipType = React.useCallback(
    async (personId: number) => {
      return await personApi.getCurrentMembership(personId);
    },
    [personApi]
  );

  // React.useEffect(() => {
  //   getMembershipRequestTypeCode(data?.membershipRequestTypeId).then((c) => setMembershipRequestType(c));
  // }, [data, getMembershipRequestTypeCode]);

  return (
    <SmallFormGenerator
      initialValues={{ ...data, year: new Date().getFullYear() }}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
      loading={loading}
      onCancel={() => history.push(ERoutes.membershipRequest)}
      saving={saving || saveChecking}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
    >
      <FGListen
        field="membershipRequestTypeId"
        onChanged={async (value) => {
          setMembershipRequestType(await getMembershipRequestTypeCode(value));
        }}
      />
      <FGListen
        field="request.personId"
        onChanged={async (value, formik) => {
          const typeId = await getMemberMembershipType(value);
          setPersonPreviousMembershipTypeId(typeId);
          console.log("selectedPersonType", typeId);
          if (formik.values.membershipInitialTypeId !== typeId && typeId) {
            formik.setFieldValue("membershipInitialTypeId", typeId);
            if (MembershipRequestType === EMembershipRequestType.Renewal) {
              formik.setFieldValue("membershipFinalTypeId", typeId);
            }
          }
        }}
      />
      <FGListen
        field="membershipInitialTypeId"
        onChanged={(value, formik) => {
          DecisionCode &&
            DecisionCode !== EMembershipRequestDecision.OtherType &&
            MembershipRequestType &&
            formik.setFieldValue("membershipFinalTypeId", value);
        }}
      />

      <FGListen
        field="membershipRequestDecisionId"
        onChanged={async (value, formik) => {
          if (value !== null) {
            const decisionCode = await getMembershipRequestDecisionCode(value);
            setDecisionCode(decisionCode);
            if (
              decisionCode === EMembershipRequestDecision.Accepted &&
              MembershipRequestType &&
              MembershipRequestType !== EMembershipRequestType.MembershipTypeChange
            ) {
              formik.setFieldValue("membershipFinalTypeId", formik.values.membershipInitialTypeId);
            }
          }
        }}
      />

      <FieldSet
        title={t(ETLCodes.RequestData)}
        rightElement={
          MembershipRequestId > 0 ? (
            <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
          ) : null
        }
      >
        <FGCustomInput label={t(ETLCodes.MembershipRequestType)} name="membershipRequestTypeId">
          {({ formik }) => (
            <RadioGroup
              inline={true}
              selectedValue={formik?.values.membershipRequestTypeId}
              onChange={(c) => {
                formik.setFieldValue("membershipRequestTypeId", +c.currentTarget.value);
              }}
              disabled={!statusEditable || data?.request?.isBlocked || !!MembershipRequestId}
            >
              {membershipRequestTypes?.map((n, index) => (
                <Radio key={index} label={n.label} value={n.value} checked={index === 0 && MembershipRequestId <= 0} />
              ))}
            </RadioGroup>
          )}
        </FGCustomInput>
        <FieldGroup columns={2}>
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              requiredMark
              showWarning={true}
              // info={<InformationTooltip tooltipMessage={ETLCodes.MustBeOldOrNewOwnerOrAnyone} />}
            />
            {MembershipRequestType !== EMembershipRequestType.MembershipTypeChange ? (
              <FGSelectInput
                name="membershipInitialTypeId"
                label={t(ETLCodes.MembershipType)}
                items={Membershiptype}
                loading={mtloading}
                readonly={
                  MembershipRequestType === EMembershipRequestType.Renewal ||
                  !statusEditable ||
                  data?.request?.isBlocked
                }
              />
            ) : (
              <FGCustomInput label={t(ETLCodes.MembershipType)}>
                {(ctx) => (
                  <Container>
                    <div style={{ width: "90%", marginRight: "1rem" }}>
                      <FGSelectInput
                        name="membershipInitialTypeId"
                        items={Membershiptype}
                        loading={mtloading}
                        readonly={true}
                      />
                    </div>
                    <i className="fas fa-arrow-right" style={{ marginTop: "0.5rem" }} />
                    <div style={{ width: "90%", marginLeft: "1rem" }}>
                      <FGSelectInput
                        name="membershipFinalTypeId"
                        items={Membershiptype}
                        loading={mtloading}
                        readonly={!statusEditable || data?.request?.isBlocked}
                      />
                    </div>
                  </Container>
                )}
              </FGCustomInput>
            )}
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            {MembershipRequestType === EMembershipRequestType.Renewal && (
              <FGTextInput label={t(ETLCodes.Year)} name="year" readonly />
            )}
          </div>
          <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
        </FieldGroup>
      </FieldSet>
      {MembershipRequestType !== EMembershipRequestType.Renewal && (
        <FieldSet title={t(ETLCodes.MeetingResult)}>
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="meetingDate"
                label={t(ETLCodes.MeetingDate)}
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGSelectInput
                name="membershipRequestDecisionId"
                label={t(ETLCodes.MeetingDecision)}
                items={Decisions}
                loading={dloading}
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              {MembershipRequestType !== EMembershipRequestType.MembershipTypeChange && (
                <FGSelectInput
                  name="membershipFinalTypeId"
                  disabled={DecisionCode !== EMembershipRequestDecision.OtherType}
                  label={t(ETLCodes.FinalMembershipType)}
                  items={Membershiptype}
                  loading={mtloading}
                  readonly={!statusEditable || data?.request?.isBlocked}
                />
              )}
            </div>
          </FieldGroup>
        </FieldSet>
      )}
      {data?.request?.status === ERequestState.EXECUTED && (
        <InlineButtonContainer>
          <div />
          <ButtonsBloc style={{ float: "right" }}>
            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.membershipRequest}`)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      )}
    </SmallFormGenerator>
  );
};
